import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"
import { ChevronDown } from "lucide-react"
import type { ReactNode } from "react"
import { SectionLabel } from "./SectionLabel"

interface CollapsibleSectionProps {
  label: ReactNode
  badge?: ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children: ReactNode
  className?: string
  /** Suppress the border between the trigger and the content. Use when the
   * content already draws its own top edge (e.g. a bordered table's header
   * row) and the two would otherwise double up. */
  hideContentBorder?: boolean
}

/**
 * Shared collapsed-by-default section used for the Fields, Additional
 * attributes, and Audit trail blocks across every master-data detail panel
 * (suppliers, articles, customers, substances, products).
 */
export function CollapsibleSection({
  label,
  badge,
  open,
  defaultOpen = false,
  onOpenChange,
  children,
  className,
  hideContentBorder = false,
}: CollapsibleSectionProps) {
  return (
    <Collapsible
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      className={cn("px-4 pb-4", className)}
    >
      <div className="overflow-hidden rounded-lg border border-grey4">
        <CollapsibleTrigger className="group flex w-full cursor-pointer items-center justify-between gap-2 bg-grey2 px-4 py-3 text-left transition hover:brightness-110">
          <div className="flex items-center gap-2">
            <SectionLabel>{label}</SectionLabel>
            {badge}
          </div>
          <ChevronDown
            size={14}
            className="shrink-0 text-grey5 transition-transform group-data-[state=open]:rotate-180"
          />
        </CollapsibleTrigger>
        <CollapsibleContent
          className={cn(!hideContentBorder && "border-t border-grey4")}
        >
          {children}
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
